/**
 * FAQ Content Configuration
 * 
 * Batch 4.4: FAQ System Implementation
 * 
 * This file contains all FAQ content for:
 * - Homepage trust cards and FAQs
 * - Full FAQ page with categories
 * - Contextual FAQs for flight/hotel pages
 * - Support FAQs for contact page
 * 
 * Content sourced from:
 * - YovoTrip_Homepage_FAQs.md
 * - YovoTrip_Full_FAQ_Content.md
 * - YovoTrip_Contextual_FAQs.md
 */

// ══════════════════════════════════════════════════════════════════════════════
// TRUST ITEMS (Non-expandable cards for reassurance and USP messaging)
// ══════════════════════════════════════════════════════════════════════════════

export const FAQ_TRUST_ITEMS = {
  homepage: [
    {
      id: 'smooth-booking',
      title: 'Smooth Booking',
      description: 'Search, review, pay, and confirm through a simple guided flow for flights and hotels.',
    },
    {
      id: 'fast-confirmation',
      title: 'Fast Confirmation Tracking',
      description: 'Your payment and booking status are tracked clearly after verification, so you know what happens next.',
    },
    {
      id: 'transparent-pricing',
      title: 'Transparent Pricing',
      description: 'Review fares, taxes, fees, and applicable policies before you make payment.',
    },
    {
      id: 'support-backed',
      title: 'Support-Backed Travel',
      description: 'Get help for booking, payment, cancellation, refund, and policy-related questions during support hours.',
    },
    {
      id: 'clear-refunds',
      title: 'Clear Cancellation Process',
      description: 'Understand cancellation rules, charges, and refund eligibility before and after booking.',
    },
    {
      id: 'flights-hotels',
      title: 'Flights + Hotels Together',
      description: 'Plan flights and stays from one place, with booking details available through YovoTrip.',
    },
    {
      id: 'mobile-app-soon',
      title: 'Mobile App Coming Soon',
      description: 'A smoother YovoTrip mobile experience is on the way — designed for quicker booking, easier trip access, offers, and support.',
    },
  ],
  flight: [
    {
      id: 'fare-review',
      title: 'Fare and Policy Review',
      description: 'Check pricing, baggage, cancellation rules before payment.',
    },
    {
      id: 'traveller-details',
      title: 'Traveller Details Matter',
      description: 'Enter names exactly as per travel documents.',
    },
    {
      id: 'booking-support',
      title: 'Support-Backed Booking',
      description: 'Help available for booking and confirmation queries.',
    },
  ],
  flightPayment: [
    {
      id: 'payment-pending',
      title: 'Payment Pending?',
      description: 'Please do not retry payment if confirmation is pending. YovoTrip verifies and assists with next steps.',
    },
  ],
  flightConfirmation: [
    {
      id: 'pnr-tracked',
      title: 'PNR/Ticket Confirmed',
      description: 'Your booking details are shown and can be accessed from My Trips.',
    },
    {
      id: 'next-steps',
      title: 'What Happens Next',
      description: 'Carry valid documents, reach airport on time, check terminal info.',
    },
  ],
  hotel: [
    {
      id: 'room-review',
      title: 'Review Room Details',
      description: 'Check room type, amenities, and guest count before booking.',
    },
    {
      id: 'cancellation-policy',
      title: 'Check Cancellation Policy',
      description: 'Free cancellation deadline and charges shown before payment.',
    },
    {
      id: 'hotel-support',
      title: 'Support-Backed Hotel Booking',
      description: 'Help available for hotel booking and confirmation queries.',
    },
  ],
  hotelConfirmation: [
    {
      id: 'booking-confirmed',
      title: 'Hotel Booking Confirmed',
      description: 'Booking details available on confirmation page and My Trips.',
    },
    {
      id: 'checkin-docs',
      title: 'Check-In Documents',
      description: 'Carry valid ID, booking confirmation, and any required documents.',
    },
  ],
};

// ══════════════════════════════════════════════════════════════════════════════
// HOMEPAGE FAQs (16 questions from YovoTrip_Homepage_FAQs.md)
// ══════════════════════════════════════════════════════════════════════════════

export const HOME_FAQS = [
  {
    id: 'home-why-yovotrip',
    question: 'Why should I book with YovoTrip?',
    answer: 'YovoTrip helps you search and book flights and hotels in one place with a smooth booking flow, transparent pricing, policy visibility before payment, and support-backed assistance after booking. Our goal is to make travel booking simpler, clearer, and more reliable for customers.',
    schemaEligible: true,
  },
  {
    id: 'home-what-makes-different',
    question: 'What makes YovoTrip different from other travel booking sites?',
    answer: 'YovoTrip focuses on clarity at every step of the journey. You can review pricing, traveller details, hotel policies, cancellation rules, and payment status before and after booking. We are building the platform around reliable booking, transparent communication, and practical support when customers need help.',
    schemaEligible: true,
  },
  {
    id: 'home-flights-hotels',
    question: 'Can I book both flights and hotels on YovoTrip?',
    answer: 'Yes. YovoTrip supports flight and hotel booking flows in one place. Availability, prices, cancellation rules, and booking conditions may vary depending on the airline, hotel, travel date, fare type, room type, and destination.',
    schemaEligible: true,
  },
  {
    id: 'home-booking-smoother',
    question: 'How does YovoTrip make booking smoother?',
    answer: 'YovoTrip is designed to keep the booking journey simple: search, compare, review details, check policies, make payment, and track confirmation. Important information such as pricing, traveller details, cancellation terms, and booking status is shown clearly during the process.',
    schemaEligible: true,
  },
  {
    id: 'home-confirmation-quickly',
    question: 'Will I receive my booking confirmation quickly?',
    answer: 'In most cases, booking confirmation is shown after successful payment and supplier confirmation. Sometimes confirmation may take slightly longer because of airline, hotel, inventory, or payment verification delays. If that happens, YovoTrip tracks the booking status and guides you on the next steps.',
    schemaEligible: true,
  },
  {
    id: 'home-payment-pending',
    question: 'What happens if my payment is successful but booking confirmation is pending?',
    answer: 'Please do not retry payment immediately. If payment is received but booking confirmation is pending, YovoTrip verifies the transaction and helps with confirmation, recovery, or the applicable refund process as per the relevant policy.',
    schemaEligible: true,
  },
  {
    id: 'home-payment-safe',
    question: 'Is my payment safe on YovoTrip?',
    answer: 'Payments are processed through a secure payment gateway. YovoTrip does not ask customers to share card PINs, banking passwords, OTPs, or sensitive payment credentials outside the official payment flow.',
    schemaEligible: true,
  },
  {
    id: 'home-final-price',
    question: 'Does YovoTrip show the final payable price before payment?',
    answer: 'Yes. YovoTrip shows the payable amount before payment, including applicable fare, hotel charges, taxes, fees, and selected add-ons where applicable. Please review the final amount carefully before completing payment.',
    schemaEligible: true,
  },
  {
    id: 'home-price-change',
    question: 'Why do flight or hotel prices change sometimes?',
    answer: 'Travel prices are dynamic. Airlines and hotels may update availability, fares, room rates, taxes, and charges in real time. The final payable amount is confirmed before payment.',
    schemaEligible: true,
  },
  {
    id: 'home-hidden-charges',
    question: 'Are there any hidden charges?',
    answer: 'YovoTrip aims to show applicable charges before payment. However, some destination-specific charges, hotel deposits, resort fees, local taxes, extra bed charges, or incidental expenses may be payable directly at the hotel if mentioned in the hotel policy or required locally.',
    schemaEligible: true,
  },
  {
    id: 'home-cancel-modify',
    question: 'Can I cancel or modify my booking?',
    answer: 'Cancellation or modification depends on the airline fare rules, hotel policy, supplier conditions, selected fare or room type, and the timing of your request. Please review the applicable cancellation and refund information before payment.',
    schemaEligible: true,
  },
  {
    id: 'home-refunds',
    question: 'How are refunds processed?',
    answer: 'If a refund is applicable, it is processed as per airline, hotel, supplier, payment gateway, and YovoTrip policy timelines. The final refundable amount may depend on cancellation charges, service fees, taxes, supplier rules, and payment method timelines.',
    schemaEligible: true,
  },
  {
    id: 'home-free-cancellation',
    question: 'What does "free cancellation" mean for hotels?',
    answer: 'Free cancellation is available only when the hotel policy shows a valid future free-cancellation deadline. After that deadline, cancellation charges may apply. If a hotel is refundable but the free window has passed, refundability will follow the applicable hotel policy.',
    schemaEligible: true,
  },
  {
    id: 'home-hotel-reliable',
    question: 'Is hotel booking reliable on YovoTrip?',
    answer: 'YovoTrip shows hotel details, room options, prices, taxes, and cancellation policies before payment wherever available. After successful payment and booking confirmation, booking details are shown on the confirmation page and/or shared through registered contact details.',
    schemaEligible: true,
  },
  {
    id: 'home-international',
    question: 'Can I book international travel on YovoTrip?',
    answer: 'YovoTrip supports domestic and international travel booking flows where inventory is available. Travellers are responsible for carrying valid passports, visas, permits, transit documents, vaccination certificates, and any other documents required by airlines, hotels, destinations, or authorities.',
    schemaEligible: true,
  },
  {
    id: 'home-policies-contact',
    question: 'Where can I read YovoTrip\'s policies or contact support?',
    answer: 'You can read YovoTrip\'s User Agreement / Terms of Use, Privacy Policy, and Cancellation & Refund Policy from the footer and booking flow. For booking, payment, cancellation, refund, or policy-related assistance, please visit the Contact Us page.',
    links: [
      { label: 'User Agreement / Terms of Use', path: '/user-agreement' },
      { label: 'Privacy Policy', path: '/privacy-policy' },
      { label: 'Cancellation & Refund Policy', path: '/cancellation-refund-policy' },
      { label: 'Contact Us', path: '/contact' },
    ],
    schemaEligible: true,
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// FULL FAQ CATEGORIES (86 questions from YovoTrip_Full_FAQ_Content.md)
// ══════════════════════════════════════════════════════════════════════════════

export const SERIES_FARE_FAQS = [
  {
    id: 'series-what-is',
    question: 'What is a Series Fare?',
    answer: 'A Series Fare is a special fare offered from airline seats that have been pre-blocked in advance, often at attractive prices.',
    schemaEligible: true,
  },
  {
    id: 'series-no-pnr-yet',
    question: "Why haven't I received an airline PNR yet?",
    answer: 'Unlike regular fares, the airline PNR for some Series Fare bookings may be generated later and, in certain cases, closer to departure. This is part of the Series Fare booking process. You can reach out to our customer care using the Booking reference id.',
    schemaEligible: true,
  },
  {
    id: 'series-concerned-pnr',
    question: 'Should I be concerned if the PNR is not available immediately?',
    answer: 'No. Delayed PNR issuance can be normal for a Series Fare. Generally, YovoTrip shares the airline PNR/e-ticket with you as soon as it is released. You can also reach out to our customer care using the Booking reference id.',
    schemaEligible: true,
  },
  {
    id: 'series-why-pnr-delay',
    question: 'Why is the PNR not available immediately?',
    answer: 'Series Fares come from pre-blocked airline inventory and follow a different ticketing process. The airline PNR may therefore be released later, sometimes closer to departure.',
    schemaEligible: true,
  },
  {
    id: 'series-booking-genuine',
    question: 'Is my Series Fare booking genuine?',
    answer: 'Yes. Series Fares are based on genuine airline inventory reserved in advance. YovoTrip tracks the booking until the PNR/e-ticket is issued.',
    schemaEligible: true,
  },
];

export const FULL_FAQ_CATEGORIES = [
  {
    id: 'booking',
    title: 'Booking with YovoTrip',
    faqs: [
      {
        id: 'full-what-is-yovotrip',
        question: 'What is YovoTrip?',
        answer: 'YovoTrip is an online travel platform for searching and booking flights and hotels. It is designed to provide a smooth booking experience, transparent pricing, policy visibility, and support-backed assistance during and after booking.',
        schemaEligible: true,
      },
      {
        id: 'full-why-book',
        question: 'Why should I book with YovoTrip?',
        answer: 'You can use YovoTrip to book flights and hotels in one place, review important policy details before payment, track booking confirmation, and contact support if you need help with payment, cancellation, refund, or booking-related queries.',
        schemaEligible: true,
      },
      {
        id: 'full-booking-process',
        question: 'How does the booking process work?',
        answer: 'The usual flow is simple: search for flights or hotels, compare options, review traveller or guest details, check pricing and policies, make payment, and receive booking confirmation after verification and supplier confirmation.',
        schemaEligible: true,
      },
      {
        id: 'full-book-for-others',
        question: 'Can I book for someone else?',
        answer: 'Yes, you may book for another traveller or guest, provided the correct traveller or guest details are entered. The person travelling must carry valid documents required by the airline, hotel, destination, or applicable authority.',
        schemaEligible: true,
      },
      {
        id: 'full-book-together',
        question: 'Can I book both flights and hotels together?',
        answer: 'YovoTrip supports both flight and hotel booking flows. Depending on the current product experience, you may book them through separate flows while managing your travel needs from the same platform.',
        schemaEligible: true,
      },
      {
        id: 'full-accurate-info',
        question: 'Why is accurate traveller or guest information important?',
        answer: 'Airlines and hotels may reject bookings, check-in, boarding, or service requests if names, ages, documents, or contact details are incorrect. Please enter details exactly as required by the airline, hotel, or travel document.',
        schemaEligible: true,
      },
      {
        id: 'full-change-details',
        question: 'Can I change traveller details after payment?',
        answer: 'Changes to traveller details depend on airline or hotel rules. Some corrections may not be allowed, and others may require charges or support intervention. Always review details carefully before payment.',
        schemaEligible: true,
      },
      {
        id: 'full-guarantee-confirmation',
        question: 'Does YovoTrip guarantee booking confirmation?',
        answer: 'YovoTrip facilitates booking based on available inventory and supplier confirmation. Confirmation depends on successful payment, availability, supplier response, and validation. If a booking cannot be confirmed, YovoTrip will guide you through the applicable recovery or refund process.',
        schemaEligible: true,
      },
    ],
  },
  {
    id: 'flights',
    title: 'Flights',
    faqs: [
      {
        id: 'full-domestic-flights',
        question: 'Can I book domestic flights on YovoTrip?',
        answer: 'Yes. You can search and book domestic flights where inventory is available. Fares, taxes, baggage, cancellation rules, and seat or meal options may vary by airline and fare type.',
        schemaEligible: true,
      },
      {
        id: 'full-international-flights',
        question: 'Can I book international flights on YovoTrip?',
        answer: 'Yes, international flight booking may be supported where inventory is available. Travellers must carry valid passports, visas, transit permits, destination documents, and any other documents required by authorities or airlines.',
        schemaEligible: true,
      },
      {
        id: 'full-pnr-ticket',
        question: 'When will I receive my flight ticket or PNR?',
        answer: 'Your PNR or ticket details are usually generated after successful payment and airline confirmation. If confirmation is delayed, please do not retry payment immediately; check the booking status or contact support.',
        schemaEligible: true,
      },
      {
        id: 'full-booking-pending',
        question: 'What does "booking pending" mean for flights?',
        answer: 'Booking pending means payment or booking verification may still be in progress, or airline confirmation has not yet completed. YovoTrip tracks such cases and helps with confirmation, recovery, or refund as applicable.',
        schemaEligible: true,
      },
      {
        id: 'full-baggage-meals-seats',
        question: 'Are baggage, meals, and seats included in my fare?',
        answer: 'Inclusions depend on the airline, route, fare type, and selected options. Some seats, meals, baggage, or add-ons may be paid, free, or unavailable. Please review the fare details before payment.',
        schemaEligible: true,
      },
      {
        id: 'full-select-seats-meals',
        question: 'Can I select seats or meals while booking?',
        answer: 'Seat and meal selection may be available depending on airline, route, and fare rules. If an option is unavailable or chargeable, it will be shown accordingly where available.',
        schemaEligible: true,
      },
      {
        id: 'full-cancel-flight',
        question: 'Can I cancel my flight booking?',
        answer: 'Flight cancellation depends on airline fare rules, route, fare type, timing, and applicable charges. Please review cancellation rules before payment and check your booking details after confirmation.',
        schemaEligible: true,
      },
      {
        id: 'full-reschedule-flight',
        question: 'Can I reschedule my flight?',
        answer: 'Rescheduling depends on airline rules, fare type, seat availability, fare difference, and applicable change fees. Some fares may be non-changeable.',
        schemaEligible: true,
      },
      {
        id: 'full-miss-flight',
        question: 'What happens if I miss my flight?',
        answer: 'No-show rules depend on the airline and fare type. Refunds, changes, or rebooking may be restricted or unavailable after no-show. Please contact support as soon as possible if you expect to miss a flight.',
        schemaEligible: true,
      },
      {
        id: 'full-infants-children',
        question: 'Can I book flights for infants and children?',
        answer: 'Yes, where airline rules permit. Infant and child fares, age rules, seat requirements, and document requirements vary by airline and route. Enter passenger details carefully.',
        schemaEligible: true,
      },
      {
        id: 'full-domestic-id',
        question: 'What ID is required for domestic flights?',
        answer: 'Domestic flight travellers may need valid government-issued identification. Requirements may vary by airline, airport, and applicable rules. Carry the same or accepted ID documents during travel.',
        schemaEligible: true,
      },
      {
        id: 'full-international-docs',
        question: 'What documents are required for international flights?',
        answer: 'International travellers are responsible for valid passports, visas, transit visas, permits, health documents, return/onward travel documents, and destination-specific requirements.',
        schemaEligible: true,
      },
    ],
  },
  {
    id: 'series-fare',
    title: 'Series Fare',
    faqs: SERIES_FARE_FAQS,
  },
  {
    id: 'hotels',
    title: 'Hotels',
    faqs: [
      {
        id: 'full-book-hotels',
        question: 'Can I book hotels on YovoTrip?',
        answer: 'Yes. You can search and book hotels where inventory is available. Room availability, pricing, taxes, amenities, meal plans, and policies may vary by hotel and date.',
        schemaEligible: true,
      },
      {
        id: 'full-hotel-confirmed',
        question: 'How do I know if my hotel booking is confirmed?',
        answer: 'After successful payment and hotel confirmation, booking details are shown on the confirmation page and/or sent to your registered contact details. Please carry the booking details during check-in.',
        schemaEligible: true,
      },
      {
        id: 'full-refundable-hotel',
        question: 'What does refundable hotel booking mean?',
        answer: 'A refundable hotel booking means cancellation may be allowed as per hotel policy. It does not always mean cancellation is free. Charges may apply based on the cancellation window and hotel rules.',
        schemaEligible: true,
      },
      {
        id: 'full-free-cancellation-hotel',
        question: 'What is free cancellation for hotels?',
        answer: 'Free cancellation is available only if the hotel policy shows a valid future free-cancellation deadline. After that deadline, cancellation charges may apply.',
        schemaEligible: true,
      },
      {
        id: 'full-free-window-expired',
        question: 'What happens if a hotel is refundable but free cancellation has expired?',
        answer: 'If the free-cancellation window has passed, cancellation may still be possible but charges may apply as per the hotel policy.',
        schemaEligible: true,
      },
      {
        id: 'full-early-late',
        question: 'Can I request early check-in or late check-out?',
        answer: 'Early check-in and late check-out depend on hotel availability and may attract extra charges. Final approval is usually at the hotel\'s discretion.',
        schemaEligible: true,
      },
      {
        id: 'full-hotel-checkin-docs',
        question: 'What documents are required at hotel check-in?',
        answer: 'Hotels may require valid government ID, passport, visa, booking confirmation, and guest details. Local rules may vary by city, hotel, nationality, and guest category.',
        schemaEligible: true,
      },
      {
        id: 'full-unmarried-couples',
        question: 'Can unmarried couples book hotels?',
        answer: 'Hotel policies vary. Some hotels may have specific guest policies or local restrictions. Please review hotel policies before booking and carry valid identification during check-in.',
        schemaEligible: true,
      },
      {
        id: 'full-extra-payment',
        question: 'What if the hotel asks for extra payment at check-in?',
        answer: 'Some charges such as security deposits, resort fees, local taxes, extra bed charges, early check-in, late check-out, or incidental expenses may be payable directly at the hotel if mentioned in the hotel policy or required locally.',
        schemaEligible: true,
      },
      {
        id: 'full-amenities-guaranteed',
        question: 'Are hotel amenities guaranteed?',
        answer: 'Amenities are based on hotel-provided information and availability. Some amenities may be seasonal, chargeable, under maintenance, or subject to hotel rules.',
        schemaEligible: true,
      },
      {
        id: 'full-hotel-confirmation-delayed',
        question: 'What happens if my hotel confirmation is delayed?',
        answer: 'If hotel confirmation is delayed after payment, please do not retry payment immediately. YovoTrip will verify the booking status and assist with confirmation, recovery, or the applicable refund process.',
        schemaEligible: true,
      },
      {
        id: 'full-cancel-hotel-after',
        question: 'Can I cancel a hotel booking after payment?',
        answer: 'Cancellation depends on the hotel policy, room type, booking date, and timing of the request. Please review the cancellation terms before payment.',
        schemaEligible: true,
      },
    ],
  },
  {
    id: 'payments',
    title: 'Payments and Pricing',
    faqs: [
      {
        id: 'full-payment-secure',
        question: 'Is online payment on YovoTrip secure?',
        answer: 'Payments are processed through a secure payment gateway. Do not share card PINs, banking passwords, OTPs, or sensitive banking credentials outside the official payment flow.',
        schemaEligible: true,
      },
      {
        id: 'full-payment-methods',
        question: 'What payment methods are supported?',
        answer: 'Available payment methods may vary depending on the payment gateway and current platform configuration. Options may include cards, UPI, net banking, wallets, or other supported methods.',
        schemaEligible: true,
      },
      {
        id: 'full-price-changed',
        question: 'Why did the price change before payment?',
        answer: 'Travel prices are dynamic. Airlines and hotels can update fares, availability, taxes, and room rates in real time. The final payable price is shown before payment.',
        schemaEligible: true,
      },
      {
        id: 'full-all-charges',
        question: 'Does YovoTrip show all charges before payment?',
        answer: 'YovoTrip aims to show applicable fares, hotel charges, taxes, fees, and selected add-ons before payment. Certain local charges or incidental expenses may be payable directly to the hotel or service provider if applicable.',
        schemaEligible: true,
      },
      {
        id: 'full-money-deducted',
        question: 'What should I do if money is deducted but booking is not confirmed?',
        answer: 'Please do not retry payment immediately. Check your booking status and contact support if confirmation does not appear. YovoTrip will verify the transaction and guide you on confirmation, recovery, or refund as applicable.',
        schemaEligible: true,
      },
      {
        id: 'full-retry-payment',
        question: 'Can I retry payment if my booking is pending?',
        answer: 'Avoid retrying payment immediately if money has already been deducted. Multiple attempts may create duplicate payment or reconciliation issues. Contact support or wait for status verification.',
        schemaEligible: true,
      },
      {
        id: 'full-payment-declined',
        question: 'Why was my payment declined?',
        answer: 'Payment may fail due to bank authorization, insufficient balance, card restrictions, OTP failure, network issues, payment gateway response, or security checks. You may try another method if no amount was deducted.',
        schemaEligible: true,
      },
      {
        id: 'full-payment-receipt',
        question: 'Will I receive a payment receipt?',
        answer: 'Booking and payment details may be shown on the confirmation page and/or sent to registered contact details where applicable. Please keep your booking reference for support queries.',
        schemaEligible: true,
      },
    ],
  },
  {
    id: 'confirmation',
    title: 'Confirmation and Tickets',
    faqs: [
      {
        id: 'full-how-know-confirmed',
        question: 'How will I know my booking is confirmed?',
        answer: 'A confirmed booking usually shows confirmation details such as PNR, booking ID, confirmation number, hotel details, or ticket details depending on the product booked.',
        schemaEligible: true,
      },
      {
        id: 'full-confirmation-time',
        question: 'Why is my confirmation taking time?',
        answer: 'Confirmation may take time due to airline or hotel response delays, payment verification, inventory refresh, system queues, or supplier validation. YovoTrip tracks such cases and helps with next steps.',
        schemaEligible: true,
      },
      {
        id: 'full-after-flight-confirm',
        question: 'What should I do after receiving flight confirmation?',
        answer: 'Review passenger names, travel date, flight timing, baggage rules, terminal information, and airline policies. Carry valid documents and reach the airport on time.',
        schemaEligible: true,
      },
      {
        id: 'full-after-hotel-confirm',
        question: 'What should I do after receiving hotel confirmation?',
        answer: 'Review hotel name, address, check-in/check-out date, guest details, room type, meal plan, cancellation policy, and ID requirements. Carry valid documents at check-in.',
        schemaEligible: true,
      },
      {
        id: 'full-find-booking-details',
        question: 'Where can I find my booking details?',
        answer: 'Booking details may be available on the confirmation page, My Trips section, or through registered contact details depending on the flow and login status.',
        links: [{ label: 'My Trips', path: '/my-trips' }],
        schemaEligible: true,
      },
      {
        id: 'full-wrong-contact',
        question: 'What if I entered a wrong email or phone number?',
        answer: 'Contact support as soon as possible. Changes may be possible depending on booking status, verification, and platform rules.',
        links: [{ label: 'Contact Us', path: '/contact' }],
        schemaEligible: true,
      },
      {
        id: 'full-download-ticket',
        question: 'Can I download or print my ticket or voucher?',
        answer: 'If available, flight ticket details may be downloaded from the confirmation page or My Trips. Hotel booking details are shown on the confirmation page and/or sent to registered contact details.',
        schemaEligible: true,
      },
      {
        id: 'full-booking-failed',
        question: 'What if my booking status shows failed?',
        answer: 'A failed booking may occur due to payment, availability, supplier, or validation issues. If payment was captured, YovoTrip will assist with applicable recovery or refund as per policy.',
        schemaEligible: true,
      },
    ],
  },
  {
    id: 'cancellation',
    title: 'Cancellation, Refunds and Rescheduling',
    faqs: [
      {
        id: 'full-how-cancel',
        question: 'How do I cancel a booking?',
        answer: 'Cancellation requests depend on the booking type. For flights, you may submit a cancellation review request from your booking details. YovoTrip will verify charges and process the request. You can also contact support for assistance.',
        schemaEligible: true,
      },
      {
        id: 'full-refundable-amount',
        question: 'How is the refundable amount calculated?',
        answer: 'The refundable amount depends on airline or hotel charges, cancellation timing, supplier rules, service fees, taxes, payment charges, and YovoTrip\'s Cancellation & Refund Policy.',
        links: [{ label: 'Cancellation & Refund Policy', path: '/cancellation-refund-policy' }],
        schemaEligible: true,
      },
      {
        id: 'full-refund-time',
        question: 'How long does a refund take?',
        answer: 'Refund timelines depend on airline, hotel, supplier, payment gateway, bank, and payment method processing. YovoTrip will help track applicable refund status where available.',
        schemaEligible: true,
      },
      {
        id: 'full-refund-lower',
        question: 'Why is my refund lower than the amount I paid?',
        answer: 'Cancellation charges, supplier penalties, service fees, non-refundable taxes, payment charges, discounts, coupons, or no-show rules may reduce the final refundable amount.',
        schemaEligible: true,
      },
      {
        id: 'full-cancel-one-passenger',
        question: 'Can I cancel only one passenger from a flight booking?',
        answer: 'Partial cancellation depends on airline rules, fare type, ticketing status, and platform support. Charges may apply.',
        schemaEligible: true,
      },
      {
        id: 'full-cancel-one-room',
        question: 'Can I cancel only one room or one guest from a hotel booking?',
        answer: 'Partial hotel cancellation depends on hotel policy, room type, supplier rules, and booking structure. Contact support if partial cancellation is needed.',
        schemaEligible: true,
      },
      {
        id: 'full-reschedule-hotel',
        question: 'Can I reschedule a hotel booking?',
        answer: 'Hotel date modifications depend on hotel availability, room rates, supplier rules, and the hotel\'s modification policy. Additional charges may apply.',
        schemaEligible: true,
      },
      {
        id: 'full-reschedule-flight-2',
        question: 'Can I reschedule a flight booking?',
        answer: 'Flight rescheduling depends on airline rules, fare type, seat availability, fare difference, and change fees. Some bookings may not allow changes.',
        schemaEligible: true,
      },
      {
        id: 'full-airline-hotel-cancels',
        question: 'What if the airline or hotel cancels my booking?',
        answer: 'If the airline or hotel cancels or changes a service, applicable options may depend on supplier policy. YovoTrip can assist with available alternatives, refund, or support guidance where applicable.',
        schemaEligible: true,
      },
      {
        id: 'full-read-cancellation-rules',
        question: 'Where can I read the cancellation rules?',
        answer: 'Cancellation information may be shown during booking and in your booking details. You can also read YovoTrip\'s Cancellation & Refund Policy from the footer or booking flow.',
        links: [{ label: 'Cancellation & Refund Policy', path: '/cancellation-refund-policy' }],
        schemaEligible: true,
      },
      {
        id: 'full-no-show',
        question: 'What does no-show mean?',
        answer: 'No-show means the traveller or guest did not report for the flight, hotel, or service as required. Refund or modification options may be restricted or unavailable depending on supplier rules.',
        schemaEligible: true,
      },
      {
        id: 'full-coupon-refund',
        question: 'Can coupon or discount amounts be refunded?',
        answer: 'Coupon, offer, wallet, or discount treatment depends on the offer terms, booking status, cancellation timing, and refund policy. Some promotional benefits may not be refundable.',
        schemaEligible: true,
      },
    ],
  },
  {
    id: 'international',
    title: 'International Travel',
    faqs: [
      {
        id: 'full-intl-flights-hotels',
        question: 'Can I book international flights and hotels on YovoTrip?',
        answer: 'YovoTrip may support international flights and hotels where inventory is available. Prices, documents, taxes, cancellation rules, and policies may vary by country and supplier.',
        schemaEligible: true,
      },
      {
        id: 'full-visa-help',
        question: 'Does YovoTrip help with visa requirements?',
        answer: 'YovoTrip may provide travel booking support, but travellers are responsible for checking and carrying valid visas, transit visas, passports, entry permits, and destination-specific documents.',
        schemaEligible: true,
      },
      {
        id: 'full-passport-validity',
        question: 'What passport validity is required for international travel?',
        answer: 'Passport validity requirements vary by destination, airline, transit country, and applicable authority. Many destinations require minimum validity beyond travel dates. Please verify before booking.',
        schemaEligible: true,
      },
      {
        id: 'full-intl-hotel-rules',
        question: 'Are international hotel rules different?',
        answer: 'Yes. International hotels may have local tax rules, resort fees, city taxes, security deposits, age policies, document requirements, and check-in rules. Review hotel policies before booking.',
        schemaEligible: true,
      },
      {
        id: 'full-currency',
        question: 'Can prices be shown in different currencies?',
        answer: 'Available currency display and payment options, if any, are shown during the booking flow. The final payable amount and payment method depend on the platform configuration and available payment options at the time of booking.',
        schemaEligible: true,
      },
      {
        id: 'full-intl-refunds',
        question: 'Are international refunds different from domestic refunds?',
        answer: 'International refunds may take longer due to airline, hotel, supplier, currency, payment gateway, and bank processing timelines. Charges and policies may also differ.',
        schemaEligible: true,
      },
      {
        id: 'full-travel-insurance',
        question: 'Do I need travel insurance for international trips?',
        answer: 'Some destinations or visa categories may require travel insurance. Optional protection products, if available, should be reviewed carefully before purchase.',
        schemaEligible: true,
      },
      {
        id: 'full-intl-hotel-docs',
        question: 'What documents should I carry for international hotel check-in?',
        answer: 'Hotels may require passports, visas, booking confirmation, payment card, local address details, or other documents depending on the country and hotel policy.',
        schemaEligible: true,
      },
    ],
  },
  {
    id: 'account',
    title: 'Account, Login and My Trips',
    faqs: [
      {
        id: 'full-need-account',
        question: 'Do I need an account to book on YovoTrip?',
        answer: 'Account requirements may depend on the booking flow. Creating an account can help manage bookings, contact details, and trip information more easily.',
        schemaEligible: true,
      },
      {
        id: 'full-my-trips',
        question: 'What does "My Trips" show?',
        answer: 'My Trips may show your booking details, status, trip information, and available actions depending on the booking type and platform flow.',
        links: [{ label: 'My Trips', path: '/my-trips' }],
        schemaEligible: true,
      },
      {
        id: 'full-cannot-login',
        question: 'I cannot log in. What should I do?',
        answer: 'Check your email, mobile number, OTP, and network connection. Use the available sign-in or verification options shown on the page. If the issue continues, contact support during support hours.',
        schemaEligible: true,
      },
      {
        id: 'full-accept-terms',
        question: 'Why do I need to accept the User Agreement / Terms of Use during signup?',
        answer: 'The User Agreement / Terms of Use explains the rules for using YovoTrip, booking travel, making payments, cancellations, refunds, supplier terms, and user responsibilities. Acceptance is required before creating an account.',
        links: [{ label: 'User Agreement / Terms of Use', path: '/user-agreement' }],
        schemaEligible: true,
      },
      {
        id: 'full-personal-info-safe',
        question: 'Is my personal information safe?',
        answer: 'YovoTrip handles personal information as described in its Privacy Policy. Please review the Privacy Policy to understand what information is collected, why it is used, how it is shared, and your rights.',
        links: [{ label: 'Privacy Policy', path: '/privacy-policy' }],
        schemaEligible: true,
      },
      {
        id: 'full-update-contact',
        question: 'Can I update my contact details?',
        answer: 'Updating contact details depends on account and booking status. If contact details linked to a booking are incorrect, contact support as soon as possible.',
        schemaEligible: true,
      },
      {
        id: 'full-mobile-app',
        question: 'Does YovoTrip have a mobile app?',
        answer: 'The YovoTrip mobile app is coming soon. For now, you can search and book flights and hotels through the YovoTrip website. Once the app is launched, we aim to make booking, trip access, offers, and support even easier on mobile.',
        schemaEligible: true,
      },
    ],
  },
  {
    id: 'offers',
    title: 'Offers, Coupons and Promotions',
    faqs: [
      {
        id: 'full-use-coupon',
        question: 'How do I use a coupon code?',
        answer: 'If a coupon field is available during booking, you may enter a valid coupon or promo code. Discount depends on offer terms, eligibility, booking value, travel date, and payment method.',
        schemaEligible: true,
      },
      {
        id: 'full-coupon-not-working',
        question: 'Why is my coupon not working?',
        answer: 'A coupon may not work if it is expired, not applicable to the selected product, below minimum booking value, not valid for the travel date, already used, or restricted by payment method or user eligibility.',
        schemaEligible: true,
      },
      {
        id: 'full-multiple-coupons',
        question: 'Can I use multiple coupons on one booking?',
        answer: 'Multiple coupons may not be allowed unless the offer terms specifically permit it. Please review the offer terms before applying.',
        schemaEligible: true,
      },
      {
        id: 'full-discounts-refundable',
        question: 'Are discounts refundable if I cancel?',
        answer: 'Discounts, coupons, cashback, or promotional benefits may be adjusted or reversed during cancellation or refund as per offer terms and the Cancellation & Refund Policy.',
        schemaEligible: true,
      },
      {
        id: 'full-offers-apply',
        question: 'Do offers apply to all flights and hotels?',
        answer: 'Offers may apply only to selected flights, hotels, routes, dates, banks, cards, payment methods, or booking values. Please check the offer terms.',
        schemaEligible: true,
      },
      {
        id: 'full-find-offers',
        question: 'Where can I find YovoTrip offers?',
        answer: 'Available offers may be shown on the YovoTrip website, booking pages, promotional banners, or offer pages where applicable. The YovoTrip mobile app is also coming soon, where eligible offers may be shown after launch.',
        schemaEligible: true,
      },
    ],
  },
  {
    id: 'support',
    title: 'Support, Grievance and Policies',
    faqs: [
      {
        id: 'full-contact-support',
        question: 'How can I contact YovoTrip support?',
        answer: 'You can contact YovoTrip through the Contact Us page for booking, payment, cancellation, refund, account, or policy-related assistance. Support hours: 9:00 AM to 6:00 PM, excluding Sundays and public holidays.',
        links: [{ label: 'Contact Us', path: '/contact' }],
        schemaEligible: true,
      },
      {
        id: 'full-support-details',
        question: 'What details should I provide when contacting support?',
        answer: 'Share your booking ID, order reference, registered contact details, travel date, issue description, and any relevant screenshots or payment reference where available. Do not share passwords, OTPs, or sensitive banking credentials.',
        schemaEligible: true,
      },
      {
        id: 'full-read-policies',
        question: 'Where can I read YovoTrip\'s policies?',
        answer: 'You can read the User Agreement / Terms of Use, Privacy Policy, and Cancellation & Refund Policy from the footer and booking flow.',
        links: [
          { label: 'User Agreement / Terms of Use', path: '/user-agreement' },
          { label: 'Privacy Policy', path: '/privacy-policy' },
          { label: 'Cancellation & Refund Policy', path: '/cancellation-refund-policy' },
        ],
        schemaEligible: true,
      },
      {
        id: 'full-raise-grievance',
        question: 'How do I raise a grievance?',
        answer: 'You can use the Contact Us or Grievance Redressal link to submit policy, privacy, refund, or service-related grievances. Grievance handling timelines are described in YovoTrip\'s policy pages.',
        links: [{ label: 'Grievance Redressal', path: '/contact#grievance' }],
        schemaEligible: true,
      },
      {
        id: 'full-support-after-booking',
        question: 'Does YovoTrip provide support after booking?',
        answer: 'Yes. YovoTrip can assist with booking confirmation, payment status, cancellation, refund, policy queries, and available support actions depending on the booking type and status.',
        schemaEligible: true,
      },
      {
        id: 'full-suspect-fraud',
        question: 'What should I do if I suspect fraud or an unsafe payment request?',
        answer: 'Do not share OTPs, passwords, card PINs, or banking credentials. Use only the official YovoTrip payment flow and contact support if you receive suspicious calls, messages, or payment requests.',
        schemaEligible: true,
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// CONTEXTUAL FAQs (from YovoTrip_Contextual_FAQs.md)
// ══════════════════════════════════════════════════════════════════════════════

// Flight Review / Preview FAQs (7 questions)
export const FLIGHT_PREVIEW_FAQS = [
  {
    id: 'ctx-flight-pnr',
    question: 'When will I receive my PNR or ticket?',
    answer: 'Your PNR or ticket details are generated after successful payment and airline confirmation. If confirmation takes longer than expected, please do not retry payment immediately; check the booking status or contact support.',
  },
  {
    id: 'ctx-flight-cancel',
    question: 'Can I cancel this flight after booking?',
    answer: 'Cancellation depends on airline fare rules, route, fare type, and timing. Applicable airline charges, service fees, taxes, and policy conditions may affect the refundable amount.',
  },
  {
    id: 'ctx-flight-reschedule',
    question: 'Can I reschedule this flight?',
    answer: 'Rescheduling depends on airline rules, fare type, seat availability, fare difference, and applicable change fees. Some fares may not allow changes.',
  },
  {
    id: 'ctx-flight-inclusions',
    question: 'Are baggage, seats, and meals included?',
    answer: 'Inclusions vary by airline, route, fare type, and selected add-ons. Review the fare details and optional services before payment.',
  },
  {
    id: 'ctx-flight-passenger-change',
    question: 'Can I change passenger details later?',
    answer: 'Passenger detail changes depend on airline rules and booking status. Some corrections may not be allowed, so please review names, age, gender, and document details carefully before payment.',
  },
  {
    id: 'ctx-flight-documents',
    question: 'What documents should I carry?',
    answer: 'Domestic travellers may need valid government ID. International travellers must carry valid passport, visa, transit documents, permits, and any destination-specific documents required by authorities or airlines.',
  },
  {
    id: 'ctx-flight-payment-pending',
    question: 'What if payment succeeds but ticketing is pending?',
    answer: 'Please do not retry payment immediately. YovoTrip verifies the payment and booking status and will assist with confirmation, recovery, or applicable refund as per policy.',
  },
];

// Flight Payment FAQs (6 questions)
export const FLIGHT_PAYMENT_FAQS = [
  {
    id: 'ctx-pay-secure',
    question: 'Is my flight payment secure?',
    answer: 'Payments are processed through a secure payment gateway. Do not share OTPs, card PINs, banking passwords, or sensitive payment information outside the official payment flow.',
  },
  {
    id: 'ctx-pay-after',
    question: 'What happens after I complete payment?',
    answer: 'After payment, YovoTrip verifies the transaction and initiates booking confirmation. Once confirmed, your PNR or ticket details are shown on the confirmation page and/or shared through registered contact details.',
  },
  {
    id: 'ctx-pay-deducted',
    question: 'What if money is deducted but the booking is not confirmed?',
    answer: 'Please do not retry payment immediately. YovoTrip will verify the payment and booking status and assist with confirmation, recovery, or applicable refund as per policy.',
  },
  {
    id: 'ctx-pay-no-retry',
    question: 'Why should I not retry payment immediately?',
    answer: 'If money has already been deducted, retrying immediately may create duplicate payment or reconciliation issues. Wait for booking status verification or contact support.',
  },
  {
    id: 'ctx-pay-failed',
    question: 'Why did my payment fail?',
    answer: 'Payment may fail because of bank authorization, OTP failure, insufficient balance, card limits, payment gateway response, network issue, or security checks.',
  },
  {
    id: 'ctx-pay-refund-fail',
    question: 'Will I get a refund if the booking fails after payment?',
    answer: 'If payment was captured but booking could not be completed, the applicable amount will be handled as per the Cancellation & Refund Policy and payment gateway/bank timelines.',
  },
];

// Flight Confirmation FAQs (6 questions)
export const FLIGHT_CONFIRMATION_FAQS = [
  {
    id: 'ctx-conf-check',
    question: 'What should I check after flight confirmation?',
    answer: 'Review passenger names, travel date, flight timing, airline, PNR, baggage rules, terminal details, and fare conditions. Carry valid documents during travel.',
  },
  {
    id: 'ctx-conf-pnr',
    question: 'Where can I find my PNR or ticket details?',
    answer: 'PNR or ticket details are shown on the confirmation page where available and may also be shared through registered contact details or My Trips.',
  },
  {
    id: 'ctx-conf-cancel',
    question: 'Can I cancel after receiving confirmation?',
    answer: 'Cancellation depends on airline rules, fare type, timing, and applicable charges. Check the booking details or contact support for available options.',
  },
  {
    id: 'ctx-conf-reschedule',
    question: 'Can I reschedule after confirmation?',
    answer: 'Rescheduling depends on airline fare rules, seat availability, fare difference, and change fees. Some fares may be non-changeable.',
  },
  {
    id: 'ctx-conf-schedule-change',
    question: 'What if airline schedule changes?',
    answer: 'Airline schedule changes are handled as per airline policy. Available options may include accepting the change, alternate options, or refund depending on airline rules.',
  },
  {
    id: 'ctx-conf-support',
    question: 'How do I contact support for this booking?',
    answer: 'Use the Contact Us page and share your booking ID, order reference, PNR, registered contact details, and issue description.',
    links: [{ label: 'Contact Us', path: '/contact' }],
  },
];

// Hotel Detail FAQs (7 questions)
export const HOTEL_DETAIL_FAQS = [
  {
    id: 'ctx-hotel-check-before',
    question: 'What should I check before booking a hotel?',
    answer: 'Review room type, guest count, check-in/check-out time, cancellation policy, taxes, meal plan, amenities, location, and hotel-specific rules before payment.',
  },
  {
    id: 'ctx-hotel-refundable',
    question: 'What does refundable hotel booking mean?',
    answer: 'Refundable means cancellation may be allowed as per hotel policy. It does not always mean cancellation is free. Charges may apply based on timing and hotel rules.',
  },
  {
    id: 'ctx-hotel-free-cancel',
    question: 'What is free cancellation?',
    answer: 'Free cancellation applies only when the hotel policy shows a valid future free-cancellation deadline. After that time, cancellation charges may apply.',
  },
  {
    id: 'ctx-hotel-amenities',
    question: 'Are hotel amenities guaranteed?',
    answer: 'Amenities are based on hotel-provided information and availability. Some amenities may be seasonal, chargeable, unavailable, or subject to maintenance.',
  },
  {
    id: 'ctx-hotel-checkin-docs',
    question: 'What documents are required at check-in?',
    answer: 'Hotels may require valid government ID, passport, visa, booking confirmation, and guest details. Rules vary by city, hotel, and nationality.',
  },
  {
    id: 'ctx-hotel-early-late',
    question: 'Can I request early check-in or late check-out?',
    answer: 'Early check-in and late check-out depend on hotel availability and may attract extra charges. Final approval is usually at the hotel\'s discretion.',
  },
  {
    id: 'ctx-hotel-extra-charges',
    question: 'What extra charges can hotels collect directly?',
    answer: 'Some hotels may collect security deposits, resort fees, city taxes, extra bed charges, local charges, or incidental expenses directly if applicable or mentioned in policy.',
  },
];

// Hotel Booking Review FAQs (7 questions)
export const HOTEL_REVIEW_FAQS = [
  {
    id: 'ctx-review-when-confirm',
    question: 'When is my hotel booking confirmed?',
    answer: 'Your hotel booking is confirmed after successful payment and hotel/supplier confirmation. Confirmation details are shown on the confirmation page and/or shared through registered contact details.',
  },
  {
    id: 'ctx-review-can-cancel',
    question: 'Can I cancel this hotel booking?',
    answer: 'Cancellation depends on the hotel policy, room type, booking date, and timing of your request. Please review the policy carefully before payment.',
  },
  {
    id: 'ctx-review-free-cancel',
    question: 'Is this hotel booking free cancellation?',
    answer: 'Free cancellation applies only if the policy shows a valid future free-cancellation deadline. Otherwise, cancellation charges may apply as per hotel policy.',
  },
  {
    id: 'ctx-review-delayed',
    question: 'What if the hotel confirmation is delayed?',
    answer: 'Please do not retry payment immediately. YovoTrip will verify the payment and booking status and assist with confirmation, recovery, or applicable refund as per policy.',
  },
  {
    id: 'ctx-review-deny-checkin',
    question: 'Can the hotel deny check-in?',
    answer: 'Hotels may deny check-in if guest details are incorrect, valid ID is not provided, policy conditions are not met, or local rules are not followed.',
  },
  {
    id: 'ctx-review-modify-guest',
    question: 'Can I modify guest details after booking?',
    answer: 'Guest detail changes depend on hotel policy, supplier rules, and booking status. Contact support as soon as possible if details need correction.',
  },
  {
    id: 'ctx-review-taxes',
    question: 'Are taxes and charges included?',
    answer: 'YovoTrip shows applicable payable charges before payment where available. Some local taxes, deposits, resort fees, or incidental charges may be payable directly at the hotel.',
  },
];

// Hotel Confirmation FAQs (6 questions)
export const HOTEL_CONFIRMATION_FAQS = [
  {
    id: 'ctx-hconf-check',
    question: 'What should I check after hotel confirmation?',
    answer: 'Review hotel name, address, check-in/check-out dates, guest details, room type, meal plan, cancellation policy, and ID requirements.',
  },
  {
    id: 'ctx-hconf-carry',
    question: 'What should I carry at check-in?',
    answer: 'Carry valid government ID or passport, visa where applicable, booking confirmation, and any documents required by the hotel or local authority.',
  },
  {
    id: 'ctx-hconf-cancel',
    question: 'Can I cancel after hotel confirmation?',
    answer: 'Cancellation depends on hotel policy, room type, and timing. Charges may apply after the free-cancellation window or for non-refundable bookings.',
  },
  {
    id: 'ctx-hconf-early-late',
    question: 'Can I request early check-in or late check-out after booking?',
    answer: 'You may request it directly with the hotel or through support where available. Approval depends on hotel availability and may involve extra charges.',
  },
  {
    id: 'ctx-hconf-extra-payment',
    question: 'What if the hotel asks for additional payment?',
    answer: 'Some local taxes, deposits, resort fees, extra guest charges, or incidental expenses may be payable directly at the hotel if applicable.',
  },
  {
    id: 'ctx-hconf-help',
    question: 'How can I get help with this hotel booking?',
    answer: 'Use the Contact Us page and share your booking ID, hotel name, check-in date, registered contact details, and issue description.',
    links: [{ label: 'Contact Us', path: '/contact' }],
  },
];

// Contact / Help Page FAQs (10 questions)
export const SUPPORT_FAQS = [
  {
    id: 'ctx-support-issues',
    question: 'What issues can YovoTrip support help with?',
    answer: 'Support can assist with booking confirmation, payment status, cancellation, refund, and policy-related questions during support hours.',
  },
  {
    id: 'ctx-support-info',
    question: 'What information should I provide to support?',
    answer: 'Please share your booking ID, order reference, registered contact details, travel date, product type, issue description, and screenshots if relevant. Do not share OTPs or banking passwords.',
  },
  {
    id: 'ctx-support-cancel-query',
    question: 'How do I raise a cancellation or refund query?',
    answer: 'Use your booking details or Contact Us page and include your booking ID, reason, and relevant travel details. Refund eligibility depends on applicable policy and supplier rules.',
  },
  {
    id: 'ctx-support-money-deducted',
    question: 'How do I report money deducted but booking not confirmed?',
    answer: 'Contact support with your order reference, payment reference if available, registered contact details, and time of payment. Do not retry payment immediately if money has already been deducted.',
  },
  {
    id: 'ctx-support-policies',
    question: 'Where can I read YovoTrip\'s policies?',
    answer: 'You can read the User Agreement / Terms of Use, Privacy Policy, and Cancellation & Refund Policy from the footer and booking flow.',
    links: [
      { label: 'User Agreement / Terms of Use', path: '/user-agreement' },
      { label: 'Privacy Policy', path: '/privacy-policy' },
      { label: 'Cancellation & Refund Policy', path: '/cancellation-refund-policy' },
    ],
  },
  {
    id: 'ctx-support-grievance',
    question: 'How do I raise a grievance?',
    answer: 'Use the Grievance Redressal link or Contact Us page. Grievance officer details and timelines are available in YovoTrip\'s policy pages.',
    links: [{ label: 'Grievance Redressal', path: '/contact#grievance' }],
  },
  {
    id: 'ctx-support-guarantee',
    question: 'Can support guarantee a refund or confirmation?',
    answer: 'Support can help verify status and guide available options, but confirmation, cancellation, and refund outcomes depend on payment status, supplier rules, airline or hotel policy, and applicable terms.',
  },
  {
    id: 'ctx-support-suspicious',
    question: 'What should I do if I receive a suspicious payment request?',
    answer: 'Do not pay outside the official YovoTrip payment flow. Do not share OTPs, passwords, card PINs, or banking credentials. Contact support if you suspect fraud.',
  },
  {
    id: 'ctx-support-intl-docs',
    question: 'Can support help with international travel documents?',
    answer: 'Support may guide you on booking-related information, but travellers are responsible for checking and carrying valid passports, visas, transit documents, and destination-specific documents.',
  },
  {
    id: 'ctx-support-booking-status',
    question: 'How can I check my booking status?',
    answer: 'Use the confirmation page, My Trips section where available, or contact support with your booking reference and registered contact details.',
    links: [{ label: 'My Trips', path: '/my-trips' }],
  },
];
